import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Sparkles } from "lucide-react";
import confetti from "canvas-confetti";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * /verify-email?token=...
 *
 * Auto-confirms on mount via POST /api/auth/verify-email and bounces the user
 * to /login with a success toast. On failure (expired/invalid) we render an
 * error state with a "Resend confirmation email" button so the user has an
 * obvious recovery path.
 */
export default function VerifyEmail() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [phase, setPhase] = useState("confirming"); // confirming | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [confirmedEmail, setConfirmedEmail] = useState("");
  const [resendEmail, setResendEmail] = useState("");
  const [resending, setResending] = useState(false);
  // Guard against React 18 StrictMode firing the effect twice in dev — the
  // second call would hit a now-consumed token and incorrectly flip the UI
  // back to "error" right after the success state.
  const firedRef = useRef(false);

  /** Multi-burst confetti spray — fires from both bottom corners, then a
   *  small center pop. Uses canvas-confetti (zero-dep, no React).
   */
  const fireConfetti = () => {
    const colors = ["#FACC15", "#F59E0B", "#FFFFFF", "#FB923C", "#A78BFA"];
    // Left edge
    confetti({
      particleCount: 60,
      spread: 70,
      startVelocity: 55,
      origin: { x: 0.05, y: 0.85 },
      angle: 60,
      colors,
      ticks: 220,
    });
    // Right edge
    confetti({
      particleCount: 60,
      spread: 70,
      startVelocity: 55,
      origin: { x: 0.95, y: 0.85 },
      angle: 120,
      colors,
      ticks: 220,
    });
    // Center pop after 250ms for an extra beat
    setTimeout(() => {
      confetti({
        particleCount: 80,
        spread: 130,
        startVelocity: 35,
        origin: { x: 0.5, y: 0.45 },
        colors,
        ticks: 200,
        scalar: 1.2,
      });
    }, 250);
  };

  useEffect(() => {
    // Dev StrictMode runs effects twice. firedRef ensures we hit the API
    // exactly once. We deliberately do NOT use a `cancelled` flag because
    // StrictMode's first cleanup would flip it to true before the response
    // resolves, leaving the page stuck on "Confirming…". Modern React
    // silently ignores state updates on unmounted components, so it's safe.
    if (firedRef.current) return;
    firedRef.current = true;
    const token = (params.get("token") || "").trim();
    if (!token) {
      setPhase("error");
      setErrorMsg("Missing confirmation token. Open the link from your email.");
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/auth/verify-email", { token });
        setConfirmedEmail(data?.email || "");
        setPhase("success");
        // Don't double-celebrate when this is just a refresh / StrictMode
        // re-fire — the backend tells us whether the bonus was actually
        // awarded *this* call vs. previously.
        const isFreshConfirm = !data?.already_verified;
        if (isFreshConfirm) {
          // Fire a multi-burst confetti spray from both bottom corners — a
          // proper celebration moment for the deferred 100-point payout.
          fireConfetti();
          toast.success("🎉 100 points unlocked — you're officially in");
        } else {
          toast.success("Email already confirmed.");
        }
        // Linger longer on a fresh confirm so the user sees the celebration.
        setTimeout(() => nav("/login"), isFreshConfirm ? 4200 : 1600);
      } catch (e) {
        const detail = e.response?.data?.detail;
        const msg = typeof detail === "string"
          ? detail
          : "This confirmation link is invalid or has expired.";
        setErrorMsg(msg);
        setPhase("error");
      }
    })();
  }, [params, nav]);

  const resend = async () => {
    if (!resendEmail || resending) return;
    setResending(true);
    try {
      await api.post("/auth/resend-verification", { email: resendEmail });
      toast.success("Confirmation email sent. Check your inbox.");
    } catch {
      toast.error("Couldn't resend right now. Try again in a minute.");
    } finally { setResending(false); }
  };

  return (
    <div data-testid="verify-email-page" className="min-h-screen flex items-center justify-center px-4 pt-20 pb-10 relative">
      <img src="https://customer-assets.emergentagent.com/job_live-broadcast-53/artifacts/xnmramec_0f112e07-9ce5-403f-a155-6e62b27aadfc.png" alt="" className="absolute inset-0 w-full h-full object-cover opacity-45" />
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black/75 to-black" />

      <div className="relative tv-glass rounded-2xl p-8 w-full max-w-md text-center">
        <img src="/tapintv-logo-v2.png" alt="Tap IN TV" className="h-40 w-auto mx-auto drop-shadow-[0_0_50px_rgba(250,204,21,0.45)]" />

        {phase === "confirming" && (
          <div className="mt-6" data-testid="verify-confirming">
            <Loader2 className="w-10 h-10 mx-auto animate-spin text-tv-accent" />
            <div className="font-display font-black text-2xl text-white mt-4">Confirming your email…</div>
            <p className="text-white/60 text-sm mt-2">This will just take a moment.</p>
          </div>
        )}

        {phase === "success" && (
          <div className="mt-6" data-testid="verify-success">
            <div className="relative inline-block">
              <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-400" />
              <Sparkles className="absolute -top-1.5 -right-2.5 w-5 h-5 text-amber-300 animate-pulse" />
            </div>
            <div className="font-display font-black text-2xl text-white mt-4">You're all set!</div>
            {confirmedEmail && (
              <p className="text-white/70 text-sm mt-2">
                <span data-testid="verify-success-email" className="text-white font-semibold">{confirmedEmail}</span> is confirmed.
              </p>
            )}
            <div
              data-testid="verify-points-banner"
              className="mt-5 mx-auto inline-flex items-center gap-2 bg-gradient-to-r from-amber-500/20 via-yellow-500/25 to-amber-500/20 border border-amber-400/50 rounded-full px-4 py-2"
            >
              <span className="text-2xl leading-none">🎉</span>
              <div className="text-left">
                <div className="text-amber-200 text-xs uppercase tracking-wider font-bold">Welcome bonus</div>
                <div className="text-white font-black text-sm">+100 points unlocked</div>
              </div>
            </div>
            <p className="text-white/60 text-xs mt-3">Redirecting you to sign in…</p>
            <Link
              to="/login"
              data-testid="verify-go-to-login"
              className="mt-5 inline-block bg-tv-accent hover:bg-yellow-400 text-black font-bold px-6 py-2.5 rounded-full"
            >
              Continue to Sign In
            </Link>
          </div>
        )}

        {phase === "error" && (
          <div className="mt-6 space-y-4" data-testid="verify-error">
            <XCircle className="w-12 h-12 mx-auto text-red-400" />
            <div className="font-display font-black text-2xl text-white">Couldn't confirm</div>
            <p className="text-white/70 text-sm" data-testid="verify-error-message">{errorMsg}</p>

            <div className="border-t border-white/10 pt-4 text-left">
              <label className="text-xs text-white/60 uppercase tracking-wider font-semibold">
                Send a fresh confirmation link
              </label>
              <input
                data-testid="verify-resend-email"
                type="email"
                placeholder="you@email.com"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white placeholder-white/30 outline-none focus:border-tv-brand"
              />
              <button
                type="button"
                data-testid="verify-resend-btn"
                onClick={resend}
                disabled={!resendEmail || resending}
                className="mt-3 w-full bg-tv-accent hover:bg-yellow-400 disabled:opacity-60 text-black font-bold py-2.5 rounded-full"
              >
                {resending ? "Sending…" : "Send confirmation email"}
              </button>
            </div>

            <div className="text-sm text-white/60">
              <Link to="/login" className="text-tv-brand hover:underline font-semibold">Back to sign in</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
